'use client';

import { useRef, useEffect, useMemo } from 'react';

interface ItemWithId {
  id: string;
}

interface UsePreviousIdsResult {
  addedIds: Set<string>;
  removedIds: Set<string>;
  isFirstRender: boolean;
}

/**
 * Tracks previous item IDs to detect additions and removals in a list.
 * Used for animating list changes - only truly new/removed items are animated.
 *
 * @param items - Array of items with id property
 * @returns Object with sets of added and removed IDs, plus first render flag
 */
export function usePreviousIds<T extends ItemWithId>(
  items: T[] | undefined
): UsePreviousIdsResult {
  const previousIdsRef = useRef<Set<string>>(new Set());
  const isFirstRenderRef = useRef(true);
  const hasSeenItemsRef = useRef(false);

  const result = useMemo(() => {
    // On first render, return empty sets
    if (isFirstRenderRef.current) {
      return {
        addedIds: new Set<string>(),
        removedIds: new Set<string>(),
        isFirstRender: true,
      };
    }

    // If items undefined or we haven't seen items yet, return empty
    // This prevents animating all items when transitioning from undefined to defined
    if (!items || !hasSeenItemsRef.current) {
      return {
        addedIds: new Set<string>(),
        removedIds: new Set<string>(),
        isFirstRender: false,
      };
    }

    const currentIds = new Set(items.map((item) => item.id));
    const previousIds = previousIdsRef.current;

    // Find IDs that are in current but not in previous (added)
    const addedIds = new Set(
      [...currentIds].filter((id) => !previousIds.has(id))
    );

    // Find IDs that are in previous but not in current (removed)
    const removedIds = new Set(
      [...previousIds].filter((id) => !currentIds.has(id))
    );

    return { addedIds, removedIds, isFirstRender: false };
  }, [items]);

  // Update previous IDs ref after render
  useEffect(() => {
    if (items) {
      previousIdsRef.current = new Set(items.map((item) => item.id));
      hasSeenItemsRef.current = true;
    }
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
    }
  }, [items]);

  return result;
}
